// astriaIndonesiaTalkTheme.ts
// ─── Astria Indonesia Talk — design tokens ─────────────────────────────────
// Ports the client's "Indo Emotional UI Pack v1" / "v3 design_system" spec
// (Ui.txt) into the same shape as SHELL_COLORS_INDONESIA_V2 in
// AstriaIndonesiaV2Shell.tsx, scoped to the "Astria Indonesia Talk"
// category's chat-card rendering only (AstriaIndonesiaTalkCard.tsx).
//
// Only the tokens needed for Tab 2 "Teman Bicara" (chat_bubble_ai,
// chat_bubble_user, persona_tag, depth_cue, input_box) are ported here — the
// other 8 tabs in Ui.txt (Daily Atmosphere, Love & Family, Life Coach, Mu &
// Culture, Primbon Light, Healing, Life Map, Relationship Flow) need
// deterministic backend content contracts that don't exist yet, so their
// tokens are intentionally left out until that lane is built.
// ─────────────────────────────────────────────────────────────────────────

export const INDONESIA_TALK_COLORS = {
  bgPrimary: "#F7F3EE",
  bgSecondary: "#FFFFFF",
  surfaceSoft: "#F9F5F0",

  accentIndigo: "#6A7BA2",
  accentSand: "#E3C9A8",
  accentRose: "#E8B7B7",
  accentPeach: "#F4D7C8",
  accentGreen: "#CFE8D9",
  accentBlue: "#D7E6F4",

  textPrimary: "#2F3440",
  textSecondary: "#6A7BA2",
  textMuted: "#8A8F9A",

  personaTagBg: "#E8DCC8",
  personaTagText: "#6A7BA2",

  chatBubbleAiBg: "#F7F3EE",
  chatBubbleUserBg: "#E3C9A8",

  shadowSoft: "0 4px 12px rgba(0,0,0,0.06)",
  shadowCard: "0 6px 18px rgba(0,0,0,0.08)",

  headerGradient: ["#F7F3EE", "#D7E6F4"] as [string, string],
};

export const INDONESIA_TALK_LAYOUT = {
  globalPadding: 24,
  sectionSpacing: 32,
  cardRadius: 18,
  bubbleRadius: 18,
  inputRadius: 24,
  personaTagRadius: 12,
  lineHeight: 1.55,
};

export const INDONESIA_TALK_ANIMATION = {
  fadeSoft: { duration: 0.25, ease: "easeOut" as const },
  slideUpSoft: { duration: 0.28, ease: "easeOut" as const },
};

// Depth cue — spec: astria_indo_emotional_os_deep_v1.depth_scaling levels,
// mapped 1:1 onto the intensity block AstriaIndonesiaTalkService.js already
// emits in the systemPrompt (shallow_or_medium / medium_or_deep /
// deep_or_very_deep) — collapsed to the 4 UI buckets from Ui.txt.
export type IndonesiaTalkDepthLevel =
  | "shallow"
  | "medium"
  | "deep"
  | "very_deep";

export const INDONESIA_TALK_DEPTH_ICON: Record<IndonesiaTalkDepthLevel, string> = {
  shallow: "wave_light",
  medium: "glow_soft",
  deep: "gradient_warm",
  very_deep: "calm_dark",
};

export const INDONESIA_TALK_DEPTH_COLOR: Record<IndonesiaTalkDepthLevel, string> = {
  shallow: INDONESIA_TALK_COLORS.accentBlue,
  medium: INDONESIA_TALK_COLORS.accentGreen,
  deep: INDONESIA_TALK_COLORS.accentSand,
  very_deep: INDONESIA_TALK_COLORS.accentIndigo,
};

// Emotional header copy — spec: Ui.txt "4. Emotional Header", keyed by the
// same low/medium/high buckets AstriaIndonesiaTalkService.js's
// detectEmotionalIntensity() already returns.
export const INDONESIA_TALK_HEADER_COPY: Record<
  "low" | "medium" | "high",
  { id: string; en: string }
> = {
  low: {
    id: "Energi kamu hari ini lembut dan tenang.",
    en: "Your energy today feels soft and calm.",
  },
  medium: {
    id: "Aku denger kamu… hari kamu lumayan berat ya.",
    en: "I hear you… your day sounds fairly heavy.",
  },
  high: {
    id: "Kita pelan-pelan ya… kamu lagi butuh tempat aman buat cerita.",
    en: "Let's go slowly… you need a safe place to talk right now.",
  },
};
